import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CatalogPaginationProps {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
  className?: string;
}

function buildPageList(page: number, pages: number): (number | '…')[] {
  if (pages <= 7) {
    return Array.from({ length: pages }, (_, i) => i + 1);
  }
  const set = new Set<number>([1, pages, page, page - 1, page + 1, page - 2, page + 2]);
  const nums = [...set].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  let prev = 0;
  for (const n of nums) {
    if (prev && n - prev > 1) out.push('…');
    out.push(n);
    prev = n;
  }
  return out;
}

export function CatalogPagination({
  page,
  pages,
  total,
  limit,
  onChange,
  className,
}: CatalogPaginationProps) {
  const effectivePages = Math.max(pages || 1, Math.ceil(total / Math.max(limit, 1)) || 1);
  if (effectivePages <= 1 || total <= limit) return null;

  const safePage = Math.min(Math.max(1, page), effectivePages);
  const from = (safePage - 1) * limit + 1;
  const to = Math.min(safePage * limit, total);
  const items = buildPageList(safePage, effectivePages);

  return (
    <nav
      className={cn(
        'mt-8 flex flex-col items-center gap-4 rounded-xl border border-surface-200 bg-white p-4 shadow-sm sm:flex-row sm:justify-between',
        className,
      )}
      aria-label="Paginación del catálogo"
    >
      <p className="text-sm text-surface-500">
        Mostrando{' '}
        <span className="font-semibold tabular-nums text-surface-800">
          {from.toLocaleString('es-MX')}–{to.toLocaleString('es-MX')}
        </span>{' '}
        de{' '}
        <span className="font-semibold tabular-nums text-surface-800">
          {total.toLocaleString('es-MX')}
        </span>
      </p>

      <div className="flex flex-wrap items-center justify-center gap-1">
        <button
          type="button"
          className="btn-secondary h-9 gap-1 px-3 text-sm"
          disabled={safePage <= 1}
          onClick={() => onChange(safePage - 1)}
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </button>

        {items.map((item, idx) =>
          item === '…' ? (
            <span key={`e-${idx}`} className="px-1.5 text-sm text-surface-400">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onChange(item)}
              aria-current={item === safePage ? 'page' : undefined}
              className={cn(
                'flex h-9 min-w-9 items-center justify-center rounded-lg px-2 text-sm font-semibold tabular-nums transition-colors',
                item === safePage
                  ? 'bg-brand-600 text-white'
                  : 'border border-surface-200 text-surface-700 hover:bg-surface-50',
              )}
            >
              {item}
            </button>
          ),
        )}

        <button
          type="button"
          className="btn-secondary h-9 gap-1 px-3 text-sm"
          disabled={safePage >= effectivePages}
          onClick={() => onChange(safePage + 1)}
          aria-label="Página siguiente"
        >
          Siguiente
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
}
